


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { faEye, faPaintRoller } from '@fortawesome/pro-duotone-svg-icons';
import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    FormContext,
    FormErrorDisplayMode,
    FormLookupsContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    IFileUploadView,
    IFormContext,
    IFormLabel,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertTitle, Box, Button, Grid2, LinearProgress, Paper, TextField, Tooltip, Typography, darken, lighten, styled } from '@mui/material';
import { ConditionMode, ValidateOn, useScopedField } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import * as Dtos from '../../../api/dtos';
import { IConsentForm } from '../../../api/dtos';
import EConsentContext, { EConsentUserType } from '../../../contexts/EConsentContext';
import useConsentVersionLookup, { PICF_VERSION_OTHER_ID } from '../../../hooks/lookups/useConsentVersionLookup';
import SourceDocuments from '../../documents/SourceDocuments';
import EConsentForm from '../EConsentForm';
import InputDisplay from '../inputs/InputDisplay';
import SelectWithTooltip from '../inputs/SelectWithTooltip';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const BoldParagraph = styled(Typography)(({theme}) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',

    [theme.breakpoints.down('sm')]: {
        width: '80%'
    }
}))

const Paragraph = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5),
}))

const StyledWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0),

    [theme.breakpoints.up('md')]: {
        maxWidth: 1000,
        margin: 'auto',
        padding: theme.spacing(3),
    }
}))

const StyledHeader = styled(Typography)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '1.25rem',
    color: theme.palette.primary.main,

    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        width: '80%'
    },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 3, 3, 3),

    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(5, 2, 5, 2),
        border: 'none',
        boxShadow: 'none'
    }
}))

const TrialInfo = styled(Alert)(({ theme }) => ({
    background: lighten(theme.palette.primary.main, 0.8),
    marginBottom: '1.5rem',
    padding: '1.5rem',
    
    '& *': {
        fontSize: '1rem'
    },
    '& > div': {
        padding: 0
    },
    '& > div > div': {
        marginBottom: '1rem'
    },
    '& > div > :last-of-type': {
        marginBottom: 0
    }
}))

const ChecklistContainer = styled(Box)(({ theme }) => ({
    marginLeft: '2rem',
    marginRight: '2rem',

    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: 0,
    }
}))

const Checklist = styled(Box)(() => ({
    display: 'flex'
}))

const ChecklistIcon = styled(Box)(({ theme }) => ({
    color: theme.palette.primary.light,
    marginRight: '1rem',
    fontSize: '1rem'
}))

const PicfButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1)
}))

const ViewButton = styled(Button)(({theme}) => ({
    padding: theme.spacing(0),
    cursor: 'pointer',
    height: 32
}))

const ViewLabel = styled('label')(({theme}) => ({
    padding: theme.spacing(0),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    height: 32
}))

const ViewContainerDiv = styled('div')(() => ({
    display: 'flex'
}))

const ViewUploadContainerDiv = styled('div')(() => ({
    flex: '1 1 auto'
}))

const ViewFailedButton = styled(Button)(({theme}) => ({
    background: theme.palette.error.main,
    color: theme.palette.common.white,

    '&:hover': {
        background: darken(theme.palette.error.main, 0.20)
    }
}))

const ViewUploadButton = styled(Button)(({theme}) => ({
    cursor: 'pointer',
    padding: theme.spacing(0, 2)
}))

const ViewProgress = styled(LinearProgress)(({theme}) => ({
    height: theme.spacing(1.5)
}))

const ViewIconButton = styled(Button)(({theme}) => ({
    minWidth: 0,
    padding: theme.spacing(0, 1.25),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: theme.spacing(2),

    '& > *': {
        opacity: 0.9
    }
}))


/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */


//const standardConsent = ({ formState }: ICrfConditionParameters<Dtos.ServicesAustraliaConsent>) => formState?.value?.consentTypeId === Dtos.ConsentTypeEnum.Standard;
const patientSigned = ({ formState }: ICrfConditionParameters<Dtos.ServicesAustraliaConsent>) => formState?.value?.patientSignedConsent === Dtos.LookupYesNoType.Yes;
const picfVersionOther = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.picfId === PICF_VERSION_OTHER_ID;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

interface IServicesAustraliaConsentProps { }

interface PicfVersionUpdaterProps {
    picfVersion: string | undefined;
}

const PicfVersionField: React.FunctionComponent = () => {
    const { data: patient } = React.useContext(PatientContext);

    const {
        state: {
            value: consentTypeId
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);
    
    const {data: picfLookup, loading: picfVersionsLoading } = useConsentVersionLookup('picfId', (patient as Dtos.Patient)?.institutionCode, 'Services');

    const [picfVersion, setPicfVersion] = React.useState<string|undefined>(undefined);

    const onPicfVersionChange = React.useCallback((picfId: number) => {
        const picfVersion = picfLookup?.items?.find(x => x.id === picfId)?.value;

        setPicfVersion(picfVersion);
    }, [picfLookup, setPicfVersion])

    const picfVersions = useMemo(() => {
        return [picfLookup];
    }, [picfLookup])

    const {
        state: {
            value: picfId
        }
    } = useScopedField<string>(`picfId`, { value: true }, false, false);

    const picfValue = React.useMemo(() => {
        return picfLookup?.items?.find(x => x.id == picfId)?.value
    }, [picfLookup, picfId]);

    return (
        <>
            <FormLookupsContext.Provider value={picfVersions}>
            {
                picfVersionsLoading ? 
                <LinearProgress color='primary' /> : 
                    <GroupedFields>
                        <GroupedField
                            propertyPath="picfId"
                            component={SelectWithTooltip}
                            onChange={(event: any) => onPicfVersionChange(event.target.value)}
                            disabled={consentTypeId === Dtos.ConsentTypeEnum.Remote}
                            FieldGridProps={{
                                size: {
                                    xl: 3,
                                    lg: 3,
                                    md: 5,
                                    sm: 12,
                                    xs: 12
                                },
                            }}
                            tooltipProps={{
                                arrow: true,
                                placement: "right",
                                title: picfValue
                            }}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={picfVersionOther}
                            subscription={{ value: true }}
                        >
                            <GroupedField
                                propertyPath="picfVersionSpecify"
                                component={Text}
                            />
                        </CrfCondition>
                    </GroupedFields>
            }
            </FormLookupsContext.Provider>

            <PicfVersionUpdater picfVersion={picfVersion} />
        </>
    );
}

const PicfVersionUpdater: React.FunctionComponent<PicfVersionUpdaterProps> = ({
    picfVersion
}) => {
    const {
        context: {
            setValue: setPicfVersion
        }
    } = useScopedField<string>(`picfVersion`, { value: true }, false, false);

    React.useEffect(() => {
        if (picfVersion)
        {
            setPicfVersion(picfVersion);
        }
    }, [picfVersion, setPicfVersion]);

    return (
        null
    );
}

const EConsentNote: React.FunctionComponent = () => {
    const {
        state: {
            value: consentType
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);

    return (
        consentType === Dtos.ConsentTypeEnum.Remote ? 
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} fixedWidth />}
                severity="info"
            >
                <AlertTitle>
                    Note
                </AlertTitle>
                <Typography>
                    The rest of the form will be completed after the patient and the investigator have completed the eConsent.
                </Typography>
            </Alert>
            :
            null
    );
}


const PatientSigningUpdater: React.FunctionComponent = () => {
    const { userType } = React.useContext(EConsentContext);

    const {
        context: {
            setValue: setPatientSigning
        }
    } = useScopedField<boolean>(`patientSigning`, {value: true}, false, false);

    const {
        context: {
            setValue: setPatientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    const {
        state: {
            value: patientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    React.useEffect(() => {
        if(userType === EConsentUserType.Participant && !patientSignedConsent)
        {
            setPatientSigning(true);
            setPatientSignedConsent(Dtos.LookupYesNoType.Yes);
        }

    }, [patientSignedConsent, setPatientSigning, setPatientSignedConsent, userType]);

    return (
        null
    );
}


const consentLookups: Dtos.ILookup[] = [
    {
        propertyName: 'participantAgreedToDisclosureOfInformation',
        items: [
            {
                id: Dtos.LookupYesNoType.Yes,
                value: 'Yes'
            },
            {
                id: Dtos.LookupYesNoType.No,
                value: 'No'
            }
        ]
    }
];


const consentLabels: IFormLabel[] = [
    {
        propertyPath: 'participantAgreedToDisclosureOfInformation',
        detailedLabel: undefined,
        label: 'I consent to the disclosure of my MBS and PBS Commonwealth health information provided by Services Australia to researchers for the purposes of the study.'
    },
    {
        propertyPath: 'patientName',
        detailedLabel: undefined,
        label: 'Name'
    },
    {
        propertyPath: 'datePatientSignedConsent',
        detailedLabel: undefined,
        label: 'Date'
    }
];

const ServicesAustraliaConsentEConsentForm: React.FunctionComponent = () => {
    const { userType: user } = React.useContext(EConsentContext);

    
    return (
        <>
            <EConsentForm
                disableHeading
                disablePaper
                labels={consentLabels}
                lookups={consentLookups}
                validateOn={ValidateOn.onChange}
                debug={AppSettings.debug}
                hideSubmit
                submitAndReturnButtonText="Submit"
            >
                <PatientSigningUpdater />
                
                <GroupedFields
                    ContainerProps={{
                        borderBottom: 'none !important',
                        borderTop: 'none !important'
                    }}
                >
                    <BoldParagraph>
                        Consent to take part in the optional parts of this trial 
                    </BoldParagraph>

                    <GroupedField
                        propertyPath="participantAgreedToDisclosureOfInformation"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 5,
                                lg: 4,
                                xl: 4
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={user === EConsentUserType.Investigator}
                    />
                    
                    <BoldParagraph>
                        Person taking part in the trial 
                    </BoldParagraph>
                    
                    <GroupedField
                        propertyPath="patientName"
                        component={InputDisplay}
                        valueConvert={(value: string, _lookups: any) => <TextField value={value?.toUpperCase()} disabled fullWidth/>}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                    />

                    <GroupedField
                        propertyPath="datePatientSignedConsent"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={user === EConsentUserType.Investigator}
                    />
                </GroupedFields>
            </EConsentForm>
        </>
    )
}

const ServicesAustraliaConsentEConsent: React.FunctionComponent = () => {
    const { userType, principalInvestigator } = React.useContext(EConsentContext);
    const { data: consentForm } = React.useContext(FormContext) as unknown as IFormContext<IConsentForm>;
    const { data: institution } = React.useContext(InstitutionContext);

    const saPicfTypeId = 3;

    const picfsDownloadLink = `/opms/picf/download/${institution?.code}/${consentForm?.picfId}/${saPicfTypeId}`;

    if(!consentForm || !userType || userType === EConsentUserType.Unknown) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Loaded
                        </AlertTitle>
                        An error occured while loading the consent form. Please report the issue to your treatment team.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    if(userType === EConsentUserType.Investigator && !consentForm.patientSignedConsent) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Available
                        </AlertTitle>
                        The consent form is still awaiting for participant's signature.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    return <>
        <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <StyledHeader>
                        <strong>{consentForm?.formDefinition?.name?.toUpperCase()}</strong>
                    </StyledHeader>
                    <TrialInfo
                        icon={false}
                    >
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Short Name of Trial</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    BCT 2401 PROSPECTIVE
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Full Name of Trial </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    A two-arm, non-randomised, prospective, multicentre study using magnetic resonance imaging (MRI) findings and pathology features to select patients with early breast cancer for omission of post-operative radiotherapy 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Principal Investigator</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {principalInvestigator}
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Trial sponsor</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    Breast Cancer Trials 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Site Name </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {institution?.name}
                                </Grid2>
                        </Grid2>
                    </TrialInfo>
                    <Paragraph>
                        Consent to release of your Medicare Benefits Schedule (MBS) and Pharmaceutical Benefits Scheme (PBS) Commonwealth health information provided by Services Australia to ​Breast Cancer Trials​ for the purpose of the ​BCT 2401 PROSPECTIVE study​ 
                    </Paragraph>
                    <Paragraph>
                        Please click the following link to view the full Services Australia Participant Information and Consent Form. Please read this information and feel free to ask any questions. You can also talk to someone you trust, like a family member, mob, friend, or your doctor. You can take some time to make up your mind. You get to decide if this trial is right for you.  
                    </Paragraph>

                    {
                        consentForm?.picfVersion && <Box style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                            <PicfButton 
                                color="primary"
                                variant="contained"
                                //@ts-expect-error
                                component="a"
                                href={picfsDownloadLink} 
                                target="_blank" 
                                rel="noopener" 
                            >
                                {consentForm?.picfVersion}
                            </PicfButton>
                        </Box>
                    }

                    <br />

                    <Paragraph>
                        I understand:  
                    </Paragraph>

                    <ChecklistContainer>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                My MBS and PBS health information will be disclosed by Services Australia for the purpose of the study. 
                            </Paragraph>
                        </Checklist>
                        
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                The results of this research may be published in articles or journals.
                            </Paragraph>
                        </Checklist>
                        
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                My name will never be disclosed by Services Australia, used in the study, or published.
                            </Paragraph>
                        </Checklist>

                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                My participation in the study is completely voluntary.
                            </Paragraph>
                        </Checklist>

                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I can withdraw my consent to release my Commonwealth health information provided by Services Australia to the study at any time (refer to the participant information sheet and withdrawal of consent form), and I do not have to provide a reason. 
                            </Paragraph>
                        </Checklist>

                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I completed the selection options below regarding participation in this optional component of PROSPECTIVE, as described in the attached Participant Information Sheet. 
                            </Paragraph>
                        </Checklist>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                The information provided to me about the study, and I have been given the opportunity to ask questions, and any questions I have asked, have been answered to my satisfaction. 
                            </Paragraph>
                        </Checklist>
                    </ChecklistContainer>
                    <ServicesAustraliaConsentEConsentForm />
                </StyledPaper>
            </StyledWrapper>
        </>
    </>
}

const FileUploadView: React.FunctionComponent<IFileUploadView> = ({
    FileUploadViewProps: {
        propertyPath,
        error,
        onCancel,
        onFailedReset,
        onUpload,
        uploading,
        progress,
        value,
        disabled,
        downloadLink,
        uploadDownloadLink,
        onRedactClick,
        redactable
    }
}) => {
    const onUploadKeyPress = React.useCallback((event?: React.KeyboardEvent<HTMLButtonElement>) => {
        event?.preventDefault();

        if (event?.charCode === 32) {
            onUpload();
        }
    }, [onUpload]);

    const onUploadClick = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();

        onUpload();
    }, [onUpload]);

    const pdfViewerViewUploadLink = uploadDownloadLink?.replace('opms', 'pdfviewer')?.replace('/download', '');

    const pdfViewerViewLink = downloadLink?.replace('opms', 'pdfviewer')?.replace('/download', '');

    if (uploading) {
        return (
            <>
                <ViewUploadButton
                    variant="contained"
                    //@ts-ignore
                    component="button"
                    fullWidth
                    color="primary"
                    onClick={onCancel}
                >
                    Cancel
                </ViewUploadButton>
                <ViewProgress
                    value={progress}
                    color="secondary"
                    variant="determinate"
                />
            </>
        );
    }

    if (error) {
        return (
            <ViewFailedButton
                variant="contained"
                //@ts-ignore
                component="button"
                fullWidth
                color="inherit"
                onClick={onFailedReset}
            >
                Upload Failed
            </ViewFailedButton>
        );
    }

    if (value?.upload) {
        return (
            <ViewContainerDiv>
                <ViewUploadContainerDiv>
                    <Button
                        variant="contained"
                        component="button"
                        fullWidth
                        color="primary"
                        onClick={onCancel}
                    >
                        Remove Upload
                    </Button>
                </ViewUploadContainerDiv>
                {
                    (value.upload.image || value.upload.extension === "pdf") && (
                        <ViewIconButton
                            variant="contained"
                            color="secondary"
                            //@ts-ignore
                            component="a"
                            href={pdfViewerViewUploadLink}
                            target="_blank"
                        >
                            <FontAwesomeIcon icon={faEye} fixedWidth />
                        </ViewIconButton>
                    )
                }
                {
                    redactable && uploadDownloadLink && (
                        <Tooltip title="Redact">
                            <ViewIconButton
                                variant="contained"
                                color="secondary"
                                onClick={() => onRedactClick(uploadDownloadLink, "", value.upload.extension)}
                            >
                                <FontAwesomeIcon icon={faPaintRoller} fixedWidth />
                            </ViewIconButton>
                        </Tooltip>
                    )
                }
            </ViewContainerDiv>
        );
    }

    return (
        <ViewContainerDiv>
            <ViewUploadContainerDiv>
                <ViewButton
                    variant="contained"
                    //@ts-ignore
                    component="button"
                    fullWidth
                    color="primary"
                    disabled={disabled}
                    onKeyDown={onUploadKeyPress}
                    onClick={onUploadClick}
                >
                    <ViewLabel
                        htmlFor={propertyPath}
                    >
                        {
                            value?.exists ?
                                'Upload New' :
                                'Upload'
                        }
                    </ViewLabel>
                </ViewButton>
            </ViewUploadContainerDiv>
            {
                !!value?.exists && (!!value?.image || value?.extension === "pdf") && downloadLink && (
                    <Tooltip title="View">
                        <ViewIconButton
                            variant="contained"
                            color="secondary"
                            //@ts-ignore
                            component="a"
                            href={pdfViewerViewLink}
                            target="_blank"
                        >
                            <FontAwesomeIcon icon={faEye} fixedWidth />
                        </ViewIconButton>
                    </Tooltip>
                )
            }
            {
                !!value?.exists && redactable && downloadLink && (
                    <Tooltip title="Redact">
                        <ViewIconButton
                            variant="contained"
                            color="secondary"
                            onClick={() => onRedactClick(downloadLink, "", value.extension)}
                        >
                            <FontAwesomeIcon icon={faPaintRoller} fixedWidth />
                        </ViewIconButton>
                    </Tooltip>
                )
            }
        </ViewContainerDiv>
    );
};

const ServicesAustraliaConsentFields: React.FunctionComponent = () => {
    
    const { state: { value: consentTypeId } } = useScopedField<Dtos.ConsentTypeEnum>('consentTypeId', { value: true}, false, false);

    const disabled = consentTypeId !== Dtos.ConsentTypeEnum.Standard
 
    return (
        <>          
            <GroupedField
                propertyPath="patientSignedConsent"
                component={ToggleButtonGroup}
                disabled={disabled}
            />

            <CrfCondition
                mode={ConditionMode.Enable}
                condition={patientSigned}
                subscription={{ value: true }}
            >
                <PicfVersionField />

                <GroupedField
                    propertyPath="datePatientSignedConsent"
                    component={DatePicker}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="participantAgreedToDisclosureOfInformation"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
            </CrfCondition>
            <SourceDocuments 
                fileUploadView={FileUploadView}
            />
        </>
    );
}


const consentTypeLookup: Dtos.ILookup[] = [
    {
        propertyName: 'consentTypeId',
        items:[
            {
                id: Dtos.ConsentTypeEnum.Remote,
                value: 'eConsent'
            },
            {
                id: Dtos.ConsentTypeEnum.Standard,
                value: 'Standard'
            }
        ]
    }
];

const ServicesAustraliaConsentForm: React.FunctionComponent<IServicesAustraliaConsentProps> = () => {
    const { data: masterGroup } = React.useContext(MasterGroupContext);
    const { data: collaboratingGroup } = React.useContext(CollaboratingGroupContext);
    const { data: institution } = React.useContext(InstitutionContext);
    const { data: patient } = React.useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const { data: form } = React.useContext(FormContext);

    const isFormComplete = useMemo(
        () => (form as Dtos.ServicesAustraliaConsent)?.saConsentStatus === Dtos.SaConsentStatus.Complete || 
            (form as Dtos.ServicesAustraliaConsent)?.saConsentStatus === Dtos.SaConsentStatus.Processed, 
        [form]
    );

    const isPatientLegible = useMemo(
        () => !!(patient?.patientStateId &&
            patient.patientStateId  > Dtos.PatientStateType.ConsentPending && 
            patient.patientStateId !== Dtos.PatientStateType.ScreenFail && 
            patient.patientStateId !== Dtos.PatientStateType.Ineligible),
        [patient]
    );
        
    const hasChanged = React.useMemo(() => {
        return form?.enteredDate !== form?.modifiedDate;
    }, [form]);

    const isEConsent = React.useMemo(() => {
        return (form as Dtos.ServicesAustraliaConsent)?.consentTypeId === Dtos.ConsentTypeEnum.Remote
    }, [form]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf || isFormComplete || !isPatientLegible}
                debug={AppSettings?.debug}
                errorMode={isEConsent ? 
                    FormErrorDisplayMode.AlwaysHideErrors :
                    hasChanged ? 
                        FormErrorDisplayMode.AlwaysShowAllErrors : 
                        FormErrorDisplayMode.AutoShowAllErrors
                }
            >
                <Typography sx={{px: 2, py: 3}}>
                    Main Consent must be completed prior to registration. Tissue Banking and Image Consent, and Services Australia Consent (AUS sites only) must be completed prior to allocation.
                </Typography>
                <FormLookupsContext.Provider value={consentTypeLookup}>
                    <GroupedField
                        propertyPath="consentTypeId"
                        component={ToggleButtonGroup}
                        LabelProps={{
                            label: <>Consent Type</>
                        }}
                        disabled
                    />
                </FormLookupsContext.Provider>

                <EConsentNote />

                <ServicesAustraliaConsentFields />
            </CrfForm>
        </PageLayout>
    );
}

const ServicesAustraliaConsent: React.FunctionComponent = () => {
    const { userType } = React.useContext(EConsentContext);

    return (
        <>
            {
                userType ? 
                    <ServicesAustraliaConsentEConsent /> :
                    <ServicesAustraliaConsentForm />
            }
        </>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ServicesAustraliaConsent;
